import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEO from '@/components/base/SEO';
import { categories } from '@/mocks/asmr';
import { getTracksBySlug, getCategoryBySlug, getCategoryTitle, type TrackItem } from '@/mocks/tracks';

const categoryImages: string[] = [
  'https://readdy.ai/api/search-image?query=Peaceful%20bedroom%20at%20night%20with%20soft%20moonlight%20and%20warm%20amber%20bedside%20lamp%2C%20cozy%20bedding%2C%20gentle%20shadows%2C%20tranquil%20sleep%20atmosphere%2C%20minimalist%20interior%20design%2C%20cream%20and%20warm%20beige%20tones&width=1920&height=500&seq=cat-header-zhumian&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Serene%20spa%20room%20with%20candles%20and%20soft%20towels%2C%20warm%20diffused%20lighting%2C%20aromatherapy%20oils%2C%20peaceful%20relaxation%20space%2C%20minimalist%20wellness%20interior%2C%20cream%20and%20soft%20gold%20tones&width=1920&height=500&seq=cat-header-jieya&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Clean%20minimalist%20study%20desk%20with%20warm%20lamp%20light%2C%20open%20notebook%2C%20wooden%20furniture%2C%20calm%20concentration%20atmosphere%2C%20quiet%20workspace%2C%20cream%20and%20oak%20tones%2C%20editorial%20photography&width=1920&height=500&seq=cat-header-zhuanzhu&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Abstract%20soft%20gradient%20texture%20with%20gentle%20waves%2C%20calming%20visual%20noise%20pattern%2C%20warm%20cream%20and%20beige%20minimalist%20abstract%20art%2C%20soothing%20aesthetic%2C%20dreamy%20soft%20focus&width=1920&height=500&seq=cat-header-baizaoyin&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Misty%20forest%20morning%20with%20soft%20sunlight%20through%20trees%2C%20gentle%20fog%2C%20lush%20green%20foliage%2C%20peaceful%20nature%20scene%2C%20tranquil%20woodland%20atmosphere%2C%20warm%20earthy%20tones%2C%20editorial%20landscape&width=1920&height=500&seq=cat-header-ziranyin&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Zen%20meditation%20space%20with%20candles%20and%20incense%20smoke%2C%20wooden%20floor%2C%20minimalist%20Japanese%20interior%2C%20peaceful%20spiritual%20atmosphere%2C%20soft%20natural%20light%2C%20warm%20cream%20and%20earthy%20brown%20tones&width=1920&height=500&seq=cat-header-mingxiang&orientation=landscape',
];

const trackCardImages: string[] = [
  'https://readdy.ai/api/search-image?query=Soft%20pillow%20and%20blanket%20in%20warm%20bedroom%20light%2C%20cozy%20sleep%20atmosphere%2C%20gentle%20amber%20glow%2C%20peaceful%20minimalist%20interior%2C%20cream%20textiles&width=800&height=500&seq=cat-track-01&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Gentle%20rain%20on%20window%20pane%20with%20warm%20indoor%20light%2C%20cozy%20evening%20mood%2C%20water%20droplets%2C%20peaceful%20atmosphere%2C%20cream%20curtains&width=800&height=500&seq=cat-track-02&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Quiet%20reading%20nook%20with%20books%20and%20warm%20lamp%2C%20wooden%20shelves%2C%20peaceful%20study%20corner%2C%20minimalist%20interior%2C%20cream%20and%20oak%20tones&width=800&height=500&seq=cat-track-03&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Soft%20candlelight%20glow%20on%20wooden%20surface%2C%20warm%20shadows%2C%20peaceful%20meditation%20setting%2C%20minimalist%20zen%20aesthetic%2C%20cream%20and%20gold%20tones&width=800&height=500&seq=cat-track-04&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Abstract%20soft%20pink%20noise%20texture%20with%20gentle%20gradients%2C%20calming%20visual%2C%20warm%20cream%20tones%2C%20soothing%20minimalist%20art&width=800&height=500&seq=cat-track-05&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Soft%20fabric%20textures%20in%20warm%20lighting%2C%20gentle%20touch%20aesthetic%2C%20cozy%20home%20atmosphere%2C%20cream%20and%20beige%20tones%2C%20minimalist%20interior&width=800&height=500&seq=cat-track-06&orientation=landscape',
];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const category = slug ? getCategoryBySlug(slug) : null;
  const tracks = slug ? getTracksBySlug(slug) : [];
  const [activeTab, setActiveTab] = useState(slug || '');

  useEffect(() => {
    if (slug) {
      setActiveTab(slug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  const handleTabChange = (newSlug: string) => {
    if (newSlug !== slug) {
      setActiveTab(newSlug);
      navigate(`/category/${newSlug}`);
    }
  };

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="font-heading text-2xl text-foreground-900 mb-4">分类未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const catIdx = categories.findIndex((c) => c.slug === slug);
  const headerImage = categoryImages[catIdx >= 0 ? catIdx : 0];

  const pageTitle = `${category.name} - ASMR免费网页`;
  const pageDesc = `${category.description}ASMR免费网页提供海量${category.name}音频在线免费收听，无需下载注册。`;

  const pageSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: pageTitle,
      description: pageDesc,
      url: `https://asmr-free.com/category/${slug}`,
      inLanguage: 'zh-CN',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://asmr-free.com/' },
          { '@type': 'ListItem', position: 2, name: category.name, item: `https://asmr-free.com/category/${slug}` },
        ],
      },
    },
  ];

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDesc}
        keywords={`ASMR${category.name},${category.name}ASMR在线免费听,免费${category.name}音频,ASMR免费网页`}
        canonicalUrl={`https://asmr-free.com/category/${slug}`}
        schemaData={pageSchema}
      />
      <Navbar />
      <main>
        <section className="relative pt-16">
          <div className="relative h-48 md:h-64 overflow-hidden">
            <img
              src={headerImage}
              alt={category.name}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center px-4">
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-2">
                  {category.name}
                </h1>
                <p className="text-background-50/70 text-sm md:text-base max-w-lg mx-auto">
                  {category.description}
                </p>
              </div>
            </div>
          </div>
        </section>

        <nav className="bg-background-50 border-b border-background-200/60" aria-label="面包屑导航">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <ol className="flex items-center gap-2 text-xs text-foreground-500">
              <li><Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-foreground-700 font-medium">{category.name}</li>
            </ol>
          </div>
        </nav>

        <section className="bg-background-50 border-b border-background-200/60 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 overflow-x-auto">
            <div className="flex items-center gap-2 min-w-max">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleTabChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 px-4 md:px-6 lg:px-8 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                全部{category.name}音频
                <span className="text-sm font-normal text-foreground-500 ml-2">共 {tracks.length} 个</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {tracks.map((track: TrackItem, idx: number) => (
                <div
                  key={track.id}
                  className="group bg-background-50 rounded-2xl overflow-hidden transition-all duration-300 hover:bg-background-200/50 cursor-pointer"
                >
                  <div className="aspect-[16/10] overflow-hidden relative">
                    <img
                      src={trackCardImages[idx % trackCardImages.length]}
                      alt={track.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                    <div className="absolute top-3 right-3 w-10 h-10 rounded-full bg-primary-500/90 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <i className="ri-play-fill text-background-50 text-lg"></i>
                    </div>
                    <div className="absolute bottom-3 left-3">
                      <span className="inline-block px-2.5 py-1 bg-background-50/90 backdrop-blur-sm text-foreground-800 text-xs rounded-full font-medium">
                        {track.duration}
                      </span>
                    </div>
                  </div>
                  <div className="p-4 md:p-5">
                    <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-1 group-hover:text-primary-600 transition-colors">
                      {track.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed mb-3 line-clamp-2">
                      {track.description}
                    </p>
                    <div className="flex items-center text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-headphone-line"></i> {(track.listens / 10000).toFixed(1)}万次收听
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}